import { message } from 'antd'
import { useRef, useState } from 'react'
import {
  markFoodSafetyDefinitionOutOfService,
  returnFoodSafetyDefinitionToService,
} from 'src/utils/foodSafetyApi'
import {
  confirmWithFocusReturn,
  returnFocusAfterDialog,
} from 'src/utils/dialogFocus'
import { formatFoodSafetySubmitError } from 'src/utils/foodSafetyFeedback'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import {
  FOOD_SAFETY_OUT_OF_SERVICE_START,
  buildFoodSafetyOutOfServiceDayOptions,
  buildFoodSafetyOutOfServiceStart,
  foodSafetyVenueToday,
  validateFoodSafetyOutOfServiceReason,
  validateFoodSafetyOutOfServiceStart,
} from 'src/utils/foodSafetyOutOfService'

/**
 * Mark out of service (reason dialog) / return to service (confirm) for a
 * fridge or freezer. Pair with `FoodSafetyOutOfServiceDialog`.
 *
 * The start is "now" unless staff say it stopped earlier: any time today, or
 * (managers only) up to 7 days back with a reason for recording it late.
 *
 * `fallbackFocus(unit)` gets focus when the button that opened the dialog is
 * gone after the change (Mark out of service / Return to service swap over).
 *
 * @param {{ venueId: string, onChanged?: Function, fallbackFocus?: Function,
 *   canManage?: boolean, timezone?: string|null }} options
 *   timezone — the venue's, from Express (day / home payload)
 */
export default function useFoodSafetyOutOfService({
  venueId,
  onChanged,
  fallbackFocus = null,
  canManage = false,
  timezone = null,
}) {
  const [unit, setUnit] = useState(null)
  const [reason, setReason] = useState('')
  const [started, setStarted] = useState(FOOD_SAFETY_OUT_OF_SERVICE_START.NOW)
  const [startDate, setStartDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [backdateReason, setBackdateReason] = useState('')
  const [error, setError] = useState(null)
  const [errorField, setErrorField] = useState(null)
  const [saving, setSaving] = useState(false)
  const openerRef = useRef(null)
  const markedUnitRef = useRef(null)
  const pendingRefreshRef = useRef(null)
  const saveLock = useFoodSafetySaveLock()

  const todayLocalDate = foodSafetyVenueToday(timezone)
  const dayOptions = buildFoodSafetyOutOfServiceDayOptions({
    todayLocalDate,
    canManage,
  })
  const startEarlier = started === FOOD_SAFETY_OUT_OF_SERVICE_START.EARLIER
  const backdated =
    startEarlier && Boolean(startDate) && startDate < todayLocalDate

  const fallbackFor = definition => () =>
    typeof fallbackFocus === 'function' ? fallbackFocus(definition) : null

  const refreshAfterChange = async () => {
    if (!(await refreshAfterFoodSafetySave(onChanged))) {
      message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
    }
  }

  const clearError = () => {
    setError(null)
    setErrorField(null)
  }

  const showError = (text, field = null) => {
    setError(text)
    setErrorField(field)
  }

  const openMark = definition => {
    markedUnitRef.current = definition
    pendingRefreshRef.current = null
    openerRef.current =
      typeof document !== 'undefined' ? document.activeElement : null
    setUnit(definition)
    setReason('')
    setStarted(FOOD_SAFETY_OUT_OF_SERVICE_START.NOW)
    setStartDate(todayLocalDate)
    setStartTime('')
    setBackdateReason('')
    clearError()
  }

  const closeMark = () => {
    if (saving) return
    setUnit(null)
  }

  // The dialog closes before the reload lands; wait so focus goes to the
  // refreshed card, not the Mark button that's about to disappear.
  const afterClose = async () => {
    await pendingRefreshRef.current
    pendingRefreshRef.current = null
    setTimeout(() =>
      returnFocusAfterDialog(
        openerRef.current,
        fallbackFor(markedUnitRef.current),
      ),
    )
  }

  const withClear = setter => value => {
    setter(value)
    clearError()
  }

  const buildBody = () => {
    const body = { reason: reason.trim() }
    if (!startEarlier) return body
    body.from = buildFoodSafetyOutOfServiceStart({
      localDate: startDate,
      time: startTime,
      timezone,
    })
    if (backdated) body.backdateReason = backdateReason.trim()
    return body
  }

  const confirmMark = async () => {
    if (!unit?._id || saveLock.isHeld('mark')) return
    const reasonError = validateFoodSafetyOutOfServiceReason(reason)
    if (reasonError) {
      showError(reasonError, 'reason')
      return
    }
    if (startEarlier) {
      const startError = validateFoodSafetyOutOfServiceStart({
        localDate: startDate,
        time: startTime,
        backdateReason,
        todayLocalDate,
        timezone,
      })
      if (startError) {
        showError(startError.message, startError.field)
        return
      }
    }
    if (!saveLock.tryAcquire('mark')) return
    setSaving(true)
    try {
      try {
        await markFoodSafetyDefinitionOutOfService(
          venueId,
          String(unit._id),
          buildBody(),
        )
      } catch (err) {
        showError(formatFoodSafetySubmitError(err))
        return
      }
      message.success(`${unit.name} marked out of service`)
      pendingRefreshRef.current = refreshAfterChange()
      setUnit(null)
      await pendingRefreshRef.current
    } finally {
      saveLock.release('mark')
      setSaving(false)
    }
  }

  const confirmReturn = definition => {
    if (!definition?._id) return
    confirmWithFocusReturn(
      {
        title: `Return ${definition.name} to service?`,
        content: 'Readings will be asked for again from now.',
        okText: 'Return to service',
        cancelText: 'Cancel',
        onOk: () =>
          saveLock.run(`return:${definition._id}`, async () => {
            try {
              await returnFoodSafetyDefinitionToService(
                venueId,
                String(definition._id),
                {},
              )
            } catch (err) {
              message.error(formatFoodSafetySubmitError(err))
              return
            }
            message.success(`${definition.name} is back in service`)
            await refreshAfterChange()
          }),
      },
      { fallbackFocus: fallbackFor(definition) },
    )
  }

  return {
    unit,
    reason,
    started,
    startDate,
    startTime,
    backdateReason,
    backdated,
    dayOptions,
    canManage,
    todayLocalDate,
    error,
    errorField,
    saving,
    openMark,
    closeMark,
    afterClose,
    updateReason: withClear(setReason),
    updateStarted: withClear(setStarted),
    updateStartDate: withClear(setStartDate),
    updateStartTime: withClear(setStartTime),
    updateBackdateReason: withClear(setBackdateReason),
    confirmMark,
    confirmReturn,
  }
}
